import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import localStorageService from '../../utils/localStorageService';
import {
  SessionList,
  CheckInForm,
  SessionStats,
  TrainingSessionForm,
  TrainingSessionCard,
  TrainingSessionDetails
} from '../../components/sessions';
import {
  CalendarDaysIcon,
  UserPlusIcon,
  ChartBarIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  PlusIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// Fonction utilitaire pour formater l'heure correctement
const formatTime = (timeString) => {
  if (!timeString) return '-';
  
  try {
    const date = new Date(timeString);
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return 'Invalid Date';
  }
};

const SessionsPage = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sessionsPerPage] = useState(5);
  
  // Multi-select state for bulk delete
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  
  // États pour les sessions de formation
  const [showAddTrainingModal, setShowAddTrainingModal] = useState(false);
  const [showTrainingDetailsModal, setShowTrainingDetailsModal] = useState(false);
  const [showEditTrainingModal, setShowEditTrainingModal] = useState(false);
  const [selectedTrainingSession, setSelectedTrainingSession] = useState(null);
  
  // État pour l'édition inline du statut
  const [editingStatus, setEditingStatus] = useState(null);
  
  // NEW: États pour le suivi des présences
  const [selectedBatchForAttendance, setSelectedBatchForAttendance] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({});
  const [showAttendanceStats, setShowAttendanceStats] = useState(false);
  
  // Récupérer les sessions actives (real data only)
  const { data: activeSessionsData } = useQuery('active-sessions', async () => {
    try {
      // Get real active sessions from localStorage
      const sessions = localStorageService.getAllItems('sessions') || [];
      const activeMembers = sessions.filter(session => session.status === 'active');
      return { activeMembers };
    } catch (error) {
      console.error('Erreur de chargement des sessions actives:', error);
      return { activeMembers: [] };
    }
  });

  // Vérifier si les données ont déjà été initialisées
  const [isDataInitialized, setIsDataInitialized] = useState(() => {
    return localStorage.getItem('sessions_initialized') === 'true';
  });
  
  // No mock sessions - using only real session data
  
  // Récupérer toutes les sessions
  const { data: sessionsData, refetch: refetchSessions } = useQuery('sessions', async () => {
    try {
      // Récupérer les sessions de localStorage (après l'initialisation potentielle)
      const sessions = localStorageService.getAllItems('sessions');
      return { sessions: sessions };
    } catch (error) {
      console.error('Erreur de chargement des sessions:', error);
      return { sessions: [] };
    }
  }, {
    // S'assurer que la query est exécutée après l'initialisation des données
    enabled: true
  });

  // NEW: Récupérer tous les groupes (batches) pour la vue planning
  const { data: batchesData, refetch: refetchBatches } = useQuery('schedule-batches', async () => {
    try {
      const batches = localStorageService.getAllBatchesWithEnrollment();
      console.log('📅 Loaded batches for schedule view:', batches);
      return { batches: batches };
    } catch (error) {
      console.error('Erreur de chargement des groupes:', error);
      return { batches: [] };
    }
  });

  // Récupérer les sessions de formation
  const { data: trainingSessionsData, refetch: refetchTrainingSessions } = useQuery('training-sessions', async () => {
    try {
      const trainingSessions = localStorageService.getAllItems('training-sessions');
      return { sessions: trainingSessions };
    } catch (error) {
      console.error('Erreur de chargement des sessions de formation:', error);
      return { sessions: [] };
    }
  });

  // Gestion du rafraîchissement des données
  const handleSessionUpdate = () => {
    // Dans un environnement réel, cela invaliderait les requêtes pour rafraîchir les données
  };

  const activeSessions = activeSessionsData?.activeMembers || [];
  const allSessions = useMemo(() => sessionsData?.sessions ?? [], [sessionsData?.sessions]);

  const formaLinkBtn = FORMA_LINK_BTN;

  const statsStrip = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    const sessionsToday = allSessions.filter((s) => {
      if (!s.checkInTime) return false;
      const t = new Date(s.checkInTime);
      return !Number.isNaN(t.getTime()) && t >= startOfDay && t <= endOfDay;
    }).length;
    const withDuration = allSessions
      .map((s) => (typeof s.duration === 'number' ? s.duration : parseFloat(String(s.duration ?? ''))))
      .filter((n) => !Number.isNaN(n) && n > 0);
    const avgMin =
      withDuration.length > 0
        ? Math.round(withDuration.reduce((a, b) => a + b, 0) / withDuration.length)
        : null;
    return [
      {
        title: "Sessions aujourd'hui",
        value: String(sessionsToday),
        hint: 'Check-in enregistrés aujourd’hui',
        icon: CalendarDaysIcon,
        iconWrap: 'bg-forma-500/15 text-forma-700 dark:bg-forma-500/20 dark:text-forma-200',
      },
      {
        title: 'Sessions actives',
        value: String(activeSessions.length),
        hint: 'Membres actuellement en salle (sans check-out)',
        icon: UserGroupIcon,
        iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
      },
      {
        title: 'Durée moyenne',
        value: avgMin != null ? `${avgMin} min` : '—',
        hint: avgMin != null ? 'Moyenne sur les sessions terminées' : 'Aucune durée enregistrée',
        icon: ClockIcon,
        iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
      },
    ];
  }, [allSessions, activeSessions.length]);

  const listTabIds = ['list', 'active', 'completed', 'cancelled'];

  const filteredSessions = useMemo(() => {
    return allSessions
      .filter((session) => {
        if (activeTab === 'list') {
          if (statusFilter === 'all') return true;
          return session.status === statusFilter;
        }
        if (activeTab === 'active') return session.status === 'active';
        if (activeTab === 'completed') return session.status === 'completed' || session.status === 'terminée';
        if (activeTab === 'cancelled') return session.status === 'cancelled';
        return true;
      })
      .filter((session) => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return true;
        const memberNameMatch = session.memberName
          ? session.memberName.toLowerCase().includes(q)
          : false;
        const idMatch = session.id ? String(session.id).toLowerCase().includes(q) : false;
        return memberNameMatch || idMatch;
      });
  }, [allSessions, activeTab, statusFilter, searchTerm]);

  // Pagination
  const indexOfLastSession = currentPage * sessionsPerPage;
  const indexOfFirstSession = indexOfLastSession - sessionsPerPage;
  const currentSessions = filteredSessions.slice(indexOfFirstSession, indexOfLastSession);
  const totalPages = Math.max(1, Math.ceil(filteredSessions.length / sessionsPerPage));
  const pageNav = paginationItems(currentPage, totalPages);
  const pageSliceLabel =
    filteredSessions.length === 0
      ? '0 session'
      : `Affichage ${indexOfFirstSession + 1}–${Math.min(indexOfLastSession, filteredSessions.length)} sur ${filteredSessions.length}`;

  // Check for URL parameters to auto-open edit modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editSessionId = searchParams.get('edit');
    
    if (editSessionId && trainingSessionsData?.sessions) {
      const sessionToEdit = trainingSessionsData.sessions.find(session => session.id === editSessionId);
      if (sessionToEdit) {
        setSelectedTrainingSession(sessionToEdit);
        setShowEditTrainingModal(true);
        setActiveTab('list'); // Make sure we're on the training sessions tab
        
        // Clean the URL after opening the modal
        navigate('/sessions', { replace: true });
      }
    }
  }, [location.search, trainingSessionsData, navigate]);
  
  // Gérer la suppression d'une session de formation
  const handleDeleteTrainingSession = (sessionId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette session de formation ?')) {
      try {
        // Supprimer de localStorage pour les deux collections
        const success1 = localStorageService.deleteItem('sessions', sessionId);
        const success2 = localStorageService.deleteItem('training-sessions', sessionId);
        
        if (success1 || success2) {
          // Mise à jour de l'état local pour sessions normales
          const updatedSessions = allSessions.filter(session => session.id !== sessionId);
          queryClient.setQueryData('sessions', { sessions: updatedSessions });
          
          // Mise à jour de l'état local pour sessions de formation
          const updatedTrainingSessions = trainingSessionsData?.sessions?.filter(session => session.id !== sessionId) || [];
          queryClient.setQueryData('training-sessions', { sessions: updatedTrainingSessions });
          
          // Afficher un message de succès
          alert('Session de formation supprimée avec succès');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de la session de formation:', error);
        alert('Erreur lors de la suppression de la session de formation');
      }
    }
  };
  
  // Gérer la visualisation d'une session de formation
  const handleViewTrainingSession = (session) => {
    setSelectedTrainingSession(session);
    setShowTrainingDetailsModal(true);
  };
  
  // Gérer l'édition d'une session de formation
  const handleEditTrainingSession = (session) => {
    setSelectedTrainingSession(session);
    setShowEditTrainingModal(true);
  };

  // Onglets
  const tabs = [
    { id: 'list', name: 'Toutes les Sessions', icon: CalendarDaysIcon },
    { id: 'schedule', name: 'Planning', icon: CalendarDaysIcon }, // NEW: Schedule view
    { id: 'attendance', name: 'Présences', icon: ClipboardDocumentCheckIcon }, // NEW: Attendance tracking
    { id: 'active', name: 'Sessions Actives', icon: ClockIcon },
    { id: 'completed', name: 'Terminées', icon: CheckCircleIcon },
    { id: 'cancelled', name: 'Annulées', icon: ExclamationTriangleIcon },
    { id: 'check-in', name: 'Enregistrer', icon: UserPlusIcon },
    { id: 'training', name: 'Formation', icon: AcademicCapIcon },
    { id: 'stats', name: 'Statistiques', icon: ChartBarIcon },
  ];

  const tabCounts = useMemo(() => {
    const ts = trainingSessionsData?.sessions || [];
    const batches = batchesData?.batches || [];
    return {
      list: allSessions.length,
      schedule: batches.length,
      attendance: allSessions.length,
      active: allSessions.filter((s) => s.status === 'active').length,
      completed: allSessions.filter((s) => s.status === 'completed' || s.status === 'terminée').length,
      cancelled: allSessions.filter((s) => s.status === 'cancelled').length,
      'check-in': activeSessions.length,
      training: ts.length,
      stats: allSessions.length,
    };
  }, [allSessions, batchesData, trainingSessionsData, activeSessions.length]);

  const sessionDistribution = useMemo(() => {
    const total = Math.max(1, allSessions.length);
    const completed = allSessions.filter((s) => s.status === 'completed' || s.status === 'terminée').length;
    const active = allSessions.filter((s) => s.status === 'active').length;
    const cancelled = allSessions.filter((s) => s.status === 'cancelled').length;
    return [
      {
        name: 'Terminées',
        count: completed,
        pillClass: 'bg-emerald-500/90',
        barClass: 'bg-gradient-to-r from-emerald-400 to-forma-600',
        pct: (completed / total) * 100,
      },
      {
        name: 'Actives',
        count: active,
        pillClass: 'bg-forma-500/90',
        barClass: 'bg-gradient-to-r from-forma-400 to-forma-700',
        pct: (active / total) * 100,
      },
      {
        name: 'Annulées',
        count: cancelled,
        pillClass: 'bg-rose-500/90',
        barClass: 'bg-gradient-to-r from-rose-400 to-red-600',
        pct: (cancelled / total) * 100,
      },
    ];
  }, [allSessions]);

  const handleDeleteSession = (sessionId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) {
      try {
        // Supprimer de localStorage pour les deux collections
        const success1 = localStorageService.deleteItem('sessions', sessionId);
        const success2 = localStorageService.deleteItem('training-sessions', sessionId);
        
        if (success1 || success2) {
          // Mise à jour de l'état local pour sessions normales
          const updatedSessions = allSessions.filter(session => session.id !== sessionId);
          queryClient.setQueryData('sessions', { sessions: updatedSessions });
          
          // Mise à jour de l'état local pour sessions de formation
          const updatedTrainingSessions = trainingSessionsData?.sessions?.filter(session => session.id !== sessionId) || [];
          queryClient.setQueryData('training-sessions', { sessions: updatedTrainingSessions });
          
          // Afficher un message de succès
          alert('Session supprimée avec succès');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de la session:', error);
        alert('Erreur lors de la suppression de la session');
      }
    }
  };

  // Multi-select functions
  const handleSelectSession = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        // Remove from selection
        return prev.filter(id => id !== sessionId);
      } else {
        // Add to selection
        return [...prev, sessionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedSessions([]);
      setIsSelectAll(false);
    } else {
      // Select all visible sessions
      const visibleSessionIds = currentSessions.map(s => s.id);
      setSelectedSessions(visibleSessionIds);
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Delete selected sessions
      let successCount = 0;
      selectedSessions.forEach(sessionId => {
        const success1 = localStorageService.deleteItem('sessions', sessionId);
        const success2 = localStorageService.deleteItem('training-sessions', sessionId);
        if (success1 || success2) successCount++;
      });
      
      // Update query cache for both collections
      const updatedSessions = allSessions.filter(session => !selectedSessions.includes(session.id));
      queryClient.setQueryData('sessions', { sessions: updatedSessions });
      
      const updatedTrainingSessions = trainingSessionsData?.sessions?.filter(session => !selectedSessions.includes(session.id)) || [];
      queryClient.setQueryData('training-sessions', { sessions: updatedTrainingSessions });
      
      // Clear selection
      setSelectedSessions([]);
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);
      
      if (successCount === selectedSessions.length) {
        alert(`${successCount} session(s) supprimée(s) avec succès`);
      } else {
        throw new Error(`Seulement ${successCount}/${selectedSessions.length} sessions ont été supprimées`);
      }
    } catch (error) {
      console.error('Error deleting sessions:', error);
      alert('Erreur lors de la suppression des sessions');
    }
  };

  // Auto-update select all state based on selected sessions
  useEffect(() => {
    if (selectedSessions.length === 0) {
      setIsSelectAll(false);
    } else {
      const visibleSessionIds = currentSessions.map(s => s.id);
      const allVisibleSelected = visibleSessionIds.every(id => selectedSessions.includes(id));
      setIsSelectAll(allVisibleSelected && visibleSessionIds.length > 0);
    }
  }, [selectedSessions, currentSessions]);

  const handleViewSession = (sessionId) => {
    const session = allSessions.find(s => s.id === sessionId);
    if (session) {
      setSelectedTrainingSession(session);
      setShowTrainingDetailsModal(true);
    }
  };

  const handleEditSession = (sessionId) => {
    const session = allSessions.find(s => s.id === sessionId);
    if (session) {
      setSelectedTrainingSession(session);
      setShowEditTrainingModal(true);
    }
  };

  const handleStatusChange = (sessionId, newStatus) => {
    try {
      // Trouver la session dans les données
      const session = allSessions.find(s => s.id === sessionId);
      if (!session) {
        console.error('Session non trouvée:', sessionId);
        return;
      }

      // Mettre à jour la session avec le nouveau statut
      const updatedSession = {
        ...session,
        status: newStatus
      };

      // Mettre à jour dans localStorage (sessions et training-sessions)
      localStorageService.updateItem('sessions', updatedSession);
      localStorageService.updateItem('training-sessions', updatedSession);

      // Mettre à jour l'état local pour forcer le re-render
      const updatedSessions = allSessions.map(s => 
        s.id === sessionId ? updatedSession : s
      );
      
      // Invalider les queries pour forcer le rafraîchissement
      queryClient.setQueryData('sessions', { sessions: updatedSessions });
      queryClient.invalidateQueries('sessions');
      queryClient.invalidateQueries('training-sessions');

      // Fermer l'éditeur de statut
      setEditingStatus(null);

      console.log(`Statut de la session ${sessionId} mis à jour: ${newStatus}`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, statusFilter]);

  const activeFiltersStrip =
    searchTerm.trim() !== '' || (activeTab === 'list' && statusFilter !== 'all');

  return (
    <>
      <div className="space-y-6">
      <PageHeader
        title="Sessions"
        description="Gestion des sessions d'entraînement et de formation"
        actions={
          <button type="button" onClick={() => setShowAddTrainingModal(true)} className={formaLinkBtn}>
            <PlusIcon className="h-5 w-5" />
            <span>Nouvelle session</span>
          </button>
        }
      />

      <UiCard padding={false} className="overflow-hidden">
        <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
          <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
            {statsStrip.map((stat, index) => (
              <motion.div
                key={stat.title}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                whileHover={{ y: -3 }}
                className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
              >
                <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                  />
                  <div className="relative flex gap-4">
                    <div
                      className={clsx(
                        'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                        stat.iconWrap
                      )}
                    >
                      <stat.icon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {stat.title}
                      </p>
                      <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                        {stat.value}
                      </p>
                      <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                        {stat.hint}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="border-b border-slate-200 bg-white/40 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/35">
          <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'inline-flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                  activeTab === tab.id
                    ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                )}
              >
                <tab.icon className="h-4 w-4" />
                <span className="whitespace-nowrap">{tab.name}</span>
                <span
                  className={clsx(
                    'ml-0.5 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                    activeTab === tab.id
                      ? 'bg-white/20 text-white'
                      : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                  )}
                >
                  {tabCounts[tab.id] ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </UiCard>

      {/* Distribution par statut + recherche (onglets liste) */}
      {listTabIds.includes(activeTab) && (
        <UiCard padding={false} className="overflow-hidden">
          <div className="border-b border-slate-200 px-6 py-5 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              Répartition par statut
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Vue d’ensemble de vos sessions enregistrées
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {sessionDistribution.map((item) => (
                <div
                  key={item.name}
                  className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-600 dark:bg-slate-800/40"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-slate-900 dark:text-slate-50">{item.name}</h4>
                    <div
                      className={clsx(
                        'rounded-full px-2 py-0.5 text-sm font-semibold text-white shadow-sm',
                        item.pillClass
                      )}
                    >
                      {item.count}
                    </div>
                  </div>
                  <div className="h-3 w-full rounded-full bg-slate-200/90 dark:bg-slate-800/80">
                    <div className={clsx('h-3 rounded-full', item.barClass)} style={{ width: `${item.pct.toFixed(1)}%` }} />
                  </div>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    {item.pct.toFixed(1)}% des sessions
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="border-b border-slate-200 bg-white/30 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/25">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative w-full md:max-w-md">
                <label htmlFor="session-search" className="sr-only">
                  Rechercher une session
                </label>
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  id="session-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Membre ou ID de session…"
                  value={searchTerm}
                  onChange={handleSearch}
                  className={clsx(
                    'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                    searchTerm.trim() ? 'pr-10' : 'pr-4'
                  )}
                />
                {searchTerm.trim() !== '' ? (
                  <button
                    type="button"
                    aria-label="Effacer la recherche"
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 shrink-0 text-slate-500 dark:text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  disabled={activeTab !== 'list'}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 md:w-auto"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actives</option>
                  <option value="completed">Terminées</option>
                  <option value="cancelled">Annulées</option>
                </select>
              </div>
            </div>
          </div>

          {activeFiltersStrip ? (
            <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
              <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                Filtres actifs
              </span>
              {searchTerm.trim() !== '' ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                  « {searchTerm.trim()} »
                </span>
              ) : null}
              {statusFilter !== 'all' && activeTab === 'list' ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                  Statut : {statusFilter}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setCurrentPage(1);
                }}
                className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Réinitialiser
              </button>
            </div>
          ) : null}
        </UiCard>
      )}

      {/* Contenu de l'onglet actif */}
      {activeTab === 'check-in' && (
        <CheckInForm 
          onSessionUpdate={handleSessionUpdate}
          activeSessions={activeSessions}
        />
      )}
      
      {activeTab === 'stats' && (
        <SessionStats />
      )}
      
      {/* Sessions de formation */}
      {activeTab === 'training' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Sessions de Formation</h3>
            <button
              onClick={() => setShowAddTrainingModal(true)}
              type="button"
              className={clsx(formaLinkBtn, 'py-2 text-sm')}
            >
              <AcademicCapIcon className="h-5 w-5" />
              <span>Nouvelle Formation</span>
            </button>
          </div>
          
          {trainingSessionsData?.sessions && trainingSessionsData.sessions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {trainingSessionsData.sessions.map(session => (
                <TrainingSessionCard
                  key={session.id}
                  session={session}
                  onView={handleViewTrainingSession}
                  onEdit={handleEditTrainingSession}
                  onDelete={handleDeleteTrainingSession}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-8 text-center">
              <AcademicCapIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">Aucune session de formation</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Vous n'avez pas encore créé de sessions de formation. Commencez par en ajouter une nouvelle.
              </p>
              <button
                onClick={() => setShowAddTrainingModal(true)}
                type="button"
                className={clsx(formaLinkBtn, 'mx-auto py-2 text-sm')}
              >
                <AcademicCapIcon className="h-5 w-5" />
                <span>Nouvelle Formation</span>
              </button>
            </div>
          )}
        </div>
      )}

         {/* NEW: Schedule View - Planning des groupes */}
         {activeTab === 'schedule' && (
           <div className="space-y-6">
             <div className="flex justify-between items-center">
               <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Planning des Cours</h3>
               <div className="flex items-center space-x-2">
                 <ClockIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                 <span className="text-slate-600 dark:text-slate-400 text-sm">Vue hebdomadaire</span>
               </div>
             </div>

             {/* Conflict Summary Widget */}
             {(() => {
               const batches = batchesData?.batches || [];
               const activeBatches = batches.filter(batch => batch.status === 'active');
               
               if (activeBatches.length === 0) return null;

               // Calculate conflict statistics
               const conflictMap = new Map();
               activeBatches.forEach(batch => {
                 const conflicts = [];
                 activeBatches.forEach(otherBatch => {
                   if (otherBatch.id === batch.id || !otherBatch.trainerId || !batch.trainerId) return;
                   if (otherBatch.trainerId !== batch.trainerId) return;
                   
                   const hasOverlappingDays = batch.days.some(day => otherBatch.days.includes(day));
                   if (!hasOverlappingDays) return;
                   
                   const batchStart = batch.startTime;
                   const batchEnd = batch.endTime;
                   const otherStart = otherBatch.startTime;
                   const otherEnd = otherBatch.endTime;
                   
                   const hasTimeOverlap = batchStart < otherEnd && batchEnd > otherStart;
                   if (!hasTimeOverlap) return;
                   
                   // Check if date periods overlap
                   const batchStartDate = new Date(batch.startDate);
                   const batchEndDate = new Date(batch.endDate);
                   const otherStartDate = new Date(otherBatch.startDate);
                   const otherEndDate = new Date(otherBatch.endDate);
                   
                   const hasDatePeriodOverlap = (
                     batchStartDate < otherEndDate && otherStartDate < batchEndDate
                   );
                   
                   if (hasDatePeriodOverlap) {
                     conflicts.push(otherBatch);
                   }
                 });
                 
                 if (conflicts.length > 0) {
                   conflictMap.set(batch.id, conflicts);
                 }
               });

               const totalConflicts = conflictMap.size;
               const conflictedTrainers = new Set();
               conflictMap.forEach((conflicts, batchId) => {
                 const batch = activeBatches.find(b => b.id === batchId);
                 if (batch?.trainerId) {
                   conflictedTrainers.add(batch.trainerId);
                 }
               });

               if (totalConflicts === 0) return null;

               return (
                 <div className="bg-red-500/10 border border-red-500/30 rounded-3xl p-4">
                   <div className="flex items-center justify-between">
                     <div className="flex items-center space-x-3">
                       <div className="bg-red-500/20 rounded-lg p-2">
                         <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                       </div>
                       <div>
                         <h4 className="text-red-300 font-medium">⚠️ Conflits d'horaires détectés</h4>
                         <p className="text-red-400/80 text-sm">
                           {totalConflicts} groupe(s) en conflit • {conflictedTrainers.size} formateur(s) concerné(s)
                         </p>
                       </div>
                     </div>
                     <div className="text-right">
                       <div className="text-red-300 text-sm font-medium">Action requise</div>
                       <div className="text-red-400/80 text-xs">Résoudre les conflits dans Cours</div>
                     </div>
                   </div>
                 </div>
               );
             })()}
          
             {/* Weekly Schedule Grid */}
             <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
               {(() => {
                 const batches = batchesData?.batches || [];
                 const activeBatches = batches.filter(batch => batch.status === 'active');
                 
                 if (activeBatches.length === 0) {
                   return (
                     <div className="text-center py-12">
                       <CalendarDaysIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
                       <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">Aucun cours planifié</h3>
                       <p className="text-slate-600 dark:text-slate-400">Créez des groupes dans la section Cours pour voir le planning</p>
                     </div>
                   );
                 }

                 // Function to detect trainer conflicts for visual indicators
                 const detectTrainerConflicts = (batch, allBatches) => {
                   const conflicts = [];
                   
                   allBatches.forEach(otherBatch => {
                     if (otherBatch.id === batch.id || !otherBatch.trainerId || !batch.trainerId) return;
                     if (otherBatch.trainerId !== batch.trainerId) return;
                     
                     // Check if they have overlapping days
                     const hasOverlappingDays = batch.days.some(day => otherBatch.days.includes(day));
                     if (!hasOverlappingDays) return;
                     
                     // Check time overlap
                     const batchStart = batch.startTime;
                     const batchEnd = batch.endTime;
                     const otherStart = otherBatch.startTime;
                     const otherEnd = otherBatch.endTime;
                     
                     const hasTimeOverlap = batchStart < otherEnd && batchEnd > otherStart;
                     if (!hasTimeOverlap) return;
                     
                     // Check if date periods overlap
                     const batchStartDate = new Date(batch.startDate);
                     const batchEndDate = new Date(batch.endDate);
                     const otherStartDate = new Date(otherBatch.startDate);
                     const otherEndDate = new Date(otherBatch.endDate);
                     
                     const hasDatePeriodOverlap = (
                       batchStartDate < otherEndDate && otherStartDate < batchEndDate
                     );
                     
                     if (hasDatePeriodOverlap) {
                       conflicts.push(otherBatch);
                     }
                   });
                   
                   return conflicts;
                 };

                 // Organize batches by day and time
                 const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                 const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
                 
                 const scheduleByDay = {};
                 weekDays.forEach(day => {
                   scheduleByDay[day] = [];
                 });

                 activeBatches.forEach(batch => {
                   batch.days.forEach(day => {
                     if (scheduleByDay[day]) {
                       scheduleByDay[day].push(batch);
                     }
                   });
                 });

                 // Sort each day's batches by start time
                 Object.keys(scheduleByDay).forEach(day => {
                   scheduleByDay[day].sort((a, b) => a.startTime.localeCompare(b.startTime));
                 });

                 return (
                   <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
                     {weekDays.map((day, index) => (
                       <div key={day} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
                         <h4 className="font-medium text-slate-900 dark:text-slate-50 mb-3 text-center">
                           {dayNames[index]}
                         </h4>
                         <div className="space-y-2">
                           {scheduleByDay[day].length === 0 ? (
                             <p className="text-slate-400 dark:text-slate-500 text-sm text-center">Aucun cours</p>
                           ) : (
                             scheduleByDay[day].map(batch => {
                               const conflicts = detectTrainerConflicts(batch, activeBatches);
                               const hasConflicts = conflicts.length > 0;
                               
                               return (
                                 <div 
                                   key={batch.id} 
                                   className={`bg-slate-50 dark:bg-slate-800/80 rounded-lg p-3 border ${
                                     hasConflicts 
                                       ? 'border-red-500/50 bg-red-500/10' 
                                       : 'border-slate-200 dark:border-slate-600'
                                   }`}
                                 >
                                   {/* Conflict warning indicator */}
                                   {hasConflicts && (
                                     <div className="flex items-center mb-2 text-red-400 text-xs">
                                       <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                       <span>⚠️ Conflit formateur</span>
                                     </div>
                                   )}
                                   
                                   <div className="text-slate-900 dark:text-slate-50 font-medium text-sm mb-1">
                                     {batch.courseName}
                                   </div>
                                   <div className="text-slate-600 dark:text-slate-400 text-xs mb-2">
                                     {batch.startTime} - {batch.endTime}
                                   </div>
                                   <div className={`text-xs mb-1 ${hasConflicts ? 'text-red-300' : 'text-slate-600 dark:text-slate-400'}`}>
                                     👨‍🏫 {batch.trainerName}
                                   </div>
                                   <div className="text-slate-600 dark:text-slate-400 text-xs mb-1">
                                     🏫 {batch.room || 'Salle TBD'}
                                   </div>
                                   <div className="flex items-center justify-between">
                                     <span className="text-slate-600 dark:text-slate-400 text-xs">
                                       👥 {batch.currentEnrollment}/{batch.maxCapacity}
                                     </span>
                                     <span className={`px-2 py-0.5 rounded text-xs ${
                                       batch.isFull ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                                     }`}>
                                       {batch.isFull ? 'Complet' : 'Ouvert'}
                                     </span>
                                   </div>
                                   
                                   {/* Show conflict details on hover or click */}
                                   {hasConflicts && (
                                     <div className="mt-2 text-xs text-red-300 bg-red-500/20 rounded p-2">
                                       <div className="font-medium mb-1">Conflits détectés:</div>
                                       {conflicts.slice(0, 2).map(conflict => (
                                         <div key={conflict.id} className="text-red-200">
                                           • {conflict.courseName} ({conflict.startTime}-{conflict.endTime})
                                         </div>
                                       ))}
                                       {conflicts.length > 2 && (
                                         <div className="text-red-200">
                                           • ... et {conflicts.length - 2} autre(s)
                                         </div>
                                       )}
                                     </div>
                                   )}
                                 </div>
                               );
                             })
                           )}
                         </div>
                       </div>
                     ))}
                   </div>
                 );
               })()}
             </div>

          {/* Daily Classes List */}
          <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Classes d'aujourd'hui</h4>
            {(() => {
              const batches = batchesData?.batches || [];
              const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
              const todayBatches = batches.filter(batch => 
                batch.status === 'active' && batch.days.includes(today)
              ).sort((a, b) => a.startTime.localeCompare(b.startTime));

              if (todayBatches.length === 0) {
                return (
                  <p className="text-slate-600 dark:text-slate-400 text-center py-8">
                    Aucun cours prévu aujourd'hui
                  </p>
                );
              }

              // Function to detect trainer conflicts (reused from weekly view)
              const detectTrainerConflicts = (batch, allBatches) => {
                const conflicts = [];
                
                allBatches.forEach(otherBatch => {
                  if (otherBatch.id === batch.id || !otherBatch.trainerId || !batch.trainerId) return;
                  if (otherBatch.trainerId !== batch.trainerId) return;
                  
                  // Check if they have overlapping days
                  const hasOverlappingDays = batch.days.some(day => otherBatch.days.includes(day));
                  if (!hasOverlappingDays) return;
                  
                  // Check time overlap
                  const batchStart = batch.startTime;
                  const batchEnd = batch.endTime;
                  const otherStart = otherBatch.startTime;
                  const otherEnd = otherBatch.endTime;
                  
                  const hasTimeOverlap = batchStart < otherEnd && batchEnd > otherStart;
                  if (!hasTimeOverlap) return;
                  
                  // Check if date periods overlap
                  const batchStartDate = new Date(batch.startDate);
                  const batchEndDate = new Date(batch.endDate);
                  const otherStartDate = new Date(otherBatch.startDate);
                  const otherEndDate = new Date(otherBatch.endDate);
                  
                  const hasDatePeriodOverlap = (
                    batchStartDate < otherEndDate && otherStartDate < batchEndDate
                  );
                  
                  if (hasDatePeriodOverlap) {
                    conflicts.push(otherBatch);
                  }
                });
                
                return conflicts;
              };

              return (
                <div className="space-y-4">
                  {todayBatches.map(batch => {
                    const conflicts = detectTrainerConflicts(batch, batches.filter(b => b.status === 'active'));
                    const hasConflicts = conflicts.length > 0;
                    
                    return (
                      <div 
                        key={batch.id} 
                        className={`rounded-lg p-4 flex items-center justify-between ${
                          hasConflicts 
                            ? 'bg-red-500/10 border border-red-500/30' 
                            : 'bg-slate-100/80 dark:bg-slate-800/40'
                        }`}
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`rounded-lg p-2 ${
                            hasConflicts 
                              ? 'bg-red-500/20' 
                              : 'bg-forma-500/15'
                          }`}>
                            {hasConflicts ? (
                              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                            ) : (
                              <ClockIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <h5 className="text-slate-900 dark:text-slate-50 font-medium">{batch.courseName}</h5>
                              {hasConflicts && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">
                                  ⚠️ Conflit
                                </span>
                              )}
                            </div>
                            <p className="text-slate-600 dark:text-slate-400 text-sm">{batch.batchName}</p>
                            {hasConflicts && (
                              <p className="text-red-300 text-xs mt-1">
                                Conflit avec {conflicts.length} autre(s) cours du formateur
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-900 dark:text-slate-50 font-medium">
                            {batch.startTime} - {batch.endTime}
                          </div>
                          <div className={`text-sm ${hasConflicts ? 'text-red-300' : 'text-slate-600 dark:text-slate-400'}`}>
                            {batch.trainerName} • {batch.room || 'Salle TBD'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-slate-900 dark:text-slate-50 text-sm">
                            {batch.currentEnrollment} étudiants
                          </div>
                          <div className={`text-xs px-2 py-1 rounded ${
                            batch.isFull ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                          }`}>
                            {batch.isFull ? 'Complet' : `${batch.availableSpots} places`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* NEW: Attendance Tracking */}
      {activeTab === 'attendance' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Suivi des Présences</h3>
            <div className="flex items-center space-x-2">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              <span className="text-slate-600 dark:text-slate-400 text-sm">Gestion quotidienne</span>
            </div>
          </div>

          {/* Batch and Date Selection */}
          <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Sélectionner un groupe
                </label>
                <select
                  value={selectedBatchForAttendance}
                  onChange={(e) => {
                    setSelectedBatchForAttendance(e.target.value);
                    setAttendanceData({}); // Reset attendance data when batch changes
                  }}
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                >
                  <option value="">Choisir un groupe</option>
                  {(() => {
                    const batches = batchesData?.batches || [];
                    const activeBatches = batches.filter(batch => batch.status === 'active');
                    return activeBatches.map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.courseName} - {batch.batchName} ({batch.currentEnrollment} étudiants)
                      </option>
                    ));
                  })()}
                </select>
              </div>

              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Date de la session
                </label>
                <input
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                />
              </div>
            </div>

            {selectedBatchForAttendance && (
              <div className="flex justify-between items-center">
                <div className="text-slate-600 dark:text-slate-400 text-sm">
                  Session sélectionnée: {new Date(attendanceDate).toLocaleDateString('fr-FR')}
                </div>
                <button
                  onClick={() => setShowAttendanceStats(!showAttendanceStats)}
                  className="px-4 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors text-sm"
                >
                  {showAttendanceStats ? 'Masquer' : 'Voir'} les statistiques
                </button>
              </div>
            )}
          </div>

          {/* Student Attendance List */}
          {selectedBatchForAttendance && (() => {
            const selectedBatch = batchesData?.batches?.find(b => b.id === selectedBatchForAttendance);
            if (!selectedBatch) return null;

            // Get enrolled students for this batch
            const enrollments = localStorageService.getBatchEnrollments(selectedBatchForAttendance);
            const students = enrollments.map(enrollment => {
              const student = localStorageService.getItemById('students', enrollment.studentId);
              return student ? { ...student, enrollmentId: enrollment.id } : null;
            }).filter(Boolean);

            if (students.length === 0) {
              return (
                <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6 text-center">
                  <UserGroupIcon className="h-16 w-16 text-slate-900 dark:text-slate-50/30 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-2">Aucun étudiant inscrit</h3>
                  <p className="text-slate-600 dark:text-slate-400">Ce groupe n'a pas encore d'étudiants inscrits.</p>
                </div>
              );
            }

            return (
              <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50">
                    Liste de présence - {selectedBatch.batchName}
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        // Mark all as present
                        const newAttendanceData = {};
                        students.forEach(student => {
                          newAttendanceData[student.id] = { attended: true, notes: '' };
                        });
                        setAttendanceData(newAttendanceData);
                      }}
                      className="px-3 py-1 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-colors text-sm"
                    >
                      Tous présents
                    </button>
                    <button
                      onClick={() => {
                        // Mark all as absent
                        const newAttendanceData = {};
                        students.forEach(student => {
                          newAttendanceData[student.id] = { attended: false, notes: '' };
                        });
                        setAttendanceData(newAttendanceData);
                      }}
                      className="px-3 py-1 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors text-sm"
                    >
                      Tous absents
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {students.map(student => (
                    <div key={student.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-forma-500/15 flex items-center justify-center">
                            <UserGroupIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                          </div>
                          <div>
                            <h5 className="text-slate-900 dark:text-slate-50 font-medium">{student.name}</h5>
                            <p className="text-slate-600 dark:text-slate-400 text-sm">{student.studentCode || student.id}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-3">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`attendance-${student.id}`}
                                checked={attendanceData[student.id]?.attended === true}
                                onChange={() => setAttendanceData(prev => ({
                                  ...prev,
                                  [student.id]: { 
                                    attended: true, 
                                    notes: prev[student.id]?.notes || '' 
                                  }
                                }))}
                                className="text-green-500 focus:ring-green-500"
                              />
                              <span className="text-green-400 text-sm">Présent</span>
                            </label>
                            
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`attendance-${student.id}`}
                                checked={attendanceData[student.id]?.attended === false}
                                onChange={() => setAttendanceData(prev => ({
                                  ...prev,
                                  [student.id]: { 
                                    attended: false, 
                                    notes: prev[student.id]?.notes || '' 
                                  }
                                }))}
                                className="text-red-500 focus:ring-red-500"
                              />
                              <span className="text-red-400 text-sm">Absent</span>
                            </label>
                          </div>
                          
                          <input
                            type="text"
                            placeholder="Notes (optionnel)"
                            value={attendanceData[student.id]?.notes || ''}
                            onChange={(e) => setAttendanceData(prev => ({
                              ...prev,
                              [student.id]: { 
                                attended: prev[student.id]?.attended, 
                                notes: e.target.value 
                              }
                            }))}
                            className="w-32 bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 text-slate-900 dark:text-slate-50 text-sm focus:outline-none focus:ring-1 focus:ring-forma-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Save Attendance Button */}
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => {
                      try {
                        // Prepare attendance data for saving
                        const attendanceRecords = students.map(student => ({
                          studentId: student.id,
                          attended: attendanceData[student.id]?.attended || false,
                          notes: attendanceData[student.id]?.notes || ''
                        }));

                        // Save attendance record
                        const record = localStorageService.createAttendanceRecord(
                          selectedBatchForAttendance,
                          attendanceDate,
                          attendanceRecords
                        );

                        console.log('✅ Attendance saved:', record);
                        
                        // Show success message
                        const presentCount = attendanceRecords.filter(r => r.attended).length;
                        const absentCount = attendanceRecords.filter(r => !r.attended).length;
                        
                        alert(`✅ Présences sauvegardées!\n✅ Présents: ${presentCount}\n❌ Absents: ${absentCount}`);
                        
                        // Refresh batches data to update attendance stats
                        refetchBatches();
                      } catch (error) {
                        console.error('Error saving attendance:', error);
                        alert('❌ Erreur lors de la sauvegarde des présences');
                      }
                    }}
                    disabled={Object.keys(attendanceData).length === 0}
                    className={clsx(formaLinkBtn, 'px-6 py-2 disabled:cursor-not-allowed disabled:opacity-50')}
                  >
                    Sauvegarder les présences
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Attendance Statistics */}
          {showAttendanceStats && selectedBatchForAttendance && (
            <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
              <h4 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Statistiques de présence</h4>
              {(() => {
                const attendanceRecords = localStorageService.getAttendanceByBatch(selectedBatchForAttendance);
                const enrollments = localStorageService.getBatchEnrollments(selectedBatchForAttendance);
                
                if (attendanceRecords.length === 0) {
                  return (
                    <p className="text-slate-600 dark:text-slate-400 text-center py-8">
                      Aucun enregistrement de présence pour ce groupe
                    </p>
                  );
                }

                const students = enrollments.map(enrollment => {
                  const student = localStorageService.getItemById('students', enrollment.studentId);
                  if (!student) return null;
                  
                  const attendanceRate = localStorageService.getStudentAttendanceRate(student.id, selectedBatchForAttendance);
                  return { ...student, attendanceRate };
                }).filter(Boolean);

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-green-400">{attendanceRecords.length}</div>
                        <div className="text-slate-600 dark:text-slate-400 text-sm">Sessions enregistrées</div>
                      </div>
                      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-blue-400">
                          {students.length > 0 ? Math.round(students.reduce((sum, s) => sum + s.attendanceRate, 0) / students.length) : 0}%
                        </div>
                        <div className="text-slate-600 dark:text-slate-400 text-sm">Taux moyen de présence</div>
                      </div>
                      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-forma-500 dark:text-forma-400">{students.length}</div>
                        <div className="text-slate-600 dark:text-slate-400 text-sm">Étudiants suivis</div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h5 className="text-slate-900 dark:text-slate-50 font-medium">Taux de présence par étudiant</h5>
                      {students.map(student => (
                        <div key={student.id} className="flex items-center justify-between bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-3">
                          <span className="text-slate-900 dark:text-slate-50">{student.name}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-24 h-2 bg-slate-50 dark:bg-slate-800/80 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  student.attendanceRate >= 80 ? 'bg-green-500' :
                                  student.attendanceRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${student.attendanceRate}%` }}
                              ></div>
                            </div>
                            <span className={`text-sm font-medium ${
                              student.attendanceRate >= 80 ? 'text-green-400' :
                              student.attendanceRate >= 60 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {student.attendanceRate.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Liste des sessions */}
      {listTabIds.includes(activeTab) && (
        <UiCard padding={false} className="overflow-hidden">
          {currentSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                <CalendarDaysIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
              </div>
              <div className="max-w-md space-y-2">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Aucune session trouvée</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Aucun résultat ne correspond à vos critères de recherche.
                </p>
              </div>
              <button type="button" onClick={() => setShowAddTrainingModal(true)} className={formaLinkBtn}>
                <PlusIcon className="h-5 w-5" />
                <span>Créer une session</span>
              </button>
            </div>
          ) : (
            <>
              {selectedSessions.length > 0 ? (
                <div className="border-b border-forma-200/60 bg-forma-50/60 px-5 py-4 dark:border-forma-900/40 dark:bg-forma-950/30">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {selectedSessions.length} session{selectedSessions.length > 1 ? 's' : ''} sélectionnée
                      {selectedSessions.length > 1 ? 's' : ''}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedSessions([]);
                          setIsSelectAll(false);
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        Désélectionner tout
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBulkDeleteModal(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Supprimer ({selectedSessions.length})
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-col gap-1 border-b border-slate-200 bg-white/50 px-5 py-3 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Sessions</p>
                <p className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{pageSliceLabel}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px]">
                  <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur dark:bg-slate-800/95">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={isSelectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        ID Session
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Membre
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Heure d&apos;entrée
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Heure de sortie
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Durée
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Statut
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {currentSessions.map((session, rowIdx) => (
                      <motion.tr
                        key={session.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={clsx(
                          'transition-colors',
                          rowIdx % 2 === 0
                            ? 'bg-white/90 dark:bg-slate-950/35'
                            : 'bg-slate-50/80 dark:bg-slate-900/45',
                          'hover:bg-forma-50/80 dark:hover:bg-forma-950/25'
                        )}
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSessions.includes(session.id)}
                            onChange={() => handleSelectSession(session.id)}
                            className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-50">
                          {session.id || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900 dark:text-slate-50">
                          {session.memberName || session.title || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-300">
                          {formatTime(session.checkInTime)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-700 dark:text-slate-300">
                          {session.checkOutTime ? formatTime(session.checkOutTime) : '-'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-900 dark:text-slate-50">
                          {session.duration ? `${session.duration} min` : 'En cours'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {editingStatus === session.id ? (
                            <select
                              value={session.status}
                              onChange={(e) => handleStatusChange(session.id, e.target.value)}
                              onBlur={() => setEditingStatus(null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Escape') {
                                  setEditingStatus(null);
                                }
                              }}
                              autoFocus
                              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-50"
                            >
                              <option value="active">Active</option>
                              <option value="completed">Terminée</option>
                              <option value="cancelled">Annulée</option>
                            </select>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingStatus(session.id)}
                              className={`rounded-full px-3 py-1 text-xs font-medium transition-all hover:opacity-80 ${
                                session.status === 'completed' || session.status === 'terminée'
                                  ? 'bg-green-500/20 text-green-400'
                                  : session.status === 'active'
                                    ? 'bg-forma-500/20 text-forma-700 dark:text-forma-300'
                                    : 'bg-red-500/20 text-red-400'
                              }`}
                              title="Cliquer pour modifier le statut"
                            >
                              {session.status === 'completed' || session.status === 'terminée'
                                ? 'Terminée'
                                : session.status === 'active'
                                  ? 'Active'
                                  : 'Annulée'}
                            </button>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleViewSession(session.id)}
                              className="rounded-lg bg-slate-100 p-2 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                              title="Voir les détails"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEditSession(session.id)}
                              className="rounded-lg bg-amber-500/15 p-2 text-amber-800 transition hover:bg-amber-500/25 dark:text-amber-200"
                              title="Modifier"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSession(session.id)}
                              className="rounded-lg bg-red-500/15 p-2 text-red-700 transition hover:bg-red-500/25 dark:text-red-300"
                              title="Supprimer"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-600 dark:text-slate-400">{pageSliceLabel}</p>
                  <nav className="flex flex-wrap items-center justify-end gap-2" aria-label="Pagination">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                      Précédent
                    </button>
                    <div className="flex flex-wrap items-center gap-1">
                      {pageNav.map((node) =>
                        node.type === 'ellipsis' ? (
                          <span
                            key={node.key}
                            className="px-2 py-1 text-sm font-medium text-slate-400"
                            aria-hidden
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={node.page}
                            type="button"
                            onClick={() => setCurrentPage(node.page)}
                            className={clsx(
                              'min-w-[2.25rem] rounded-lg px-2 py-1 text-sm font-semibold transition',
                              currentPage === node.page
                                ? 'bg-forma-600 text-white shadow-sm'
                                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                            )}
                          >
                            {node.page}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Suivant
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    </button>
                  </nav>
                </div>
              ) : null}
            </>
          )}
        </UiCard>
      )}
      
      {/* Modals pour les sessions de formation */}
      {showAddTrainingModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm dark:bg-black/75 z-50 flex items-start justify-center overflow-y-auto">
          <div className="w-full max-w-4xl p-4 pt-8 pb-8">
          <TrainingSessionForm
            onClose={() => setShowAddTrainingModal(false)}
            onSuccess={() => {
              setShowAddTrainingModal(false);
              queryClient.invalidateQueries('training-sessions');
            }}
          />
          </div>
        </div>
      )}

      {showTrainingDetailsModal && selectedTrainingSession && (
        <TrainingSessionDetails
          session={selectedTrainingSession}
          onClose={() => setShowTrainingDetailsModal(false)}
          onEdit={handleEditTrainingSession}
        />
      )}

      {showEditTrainingModal && selectedTrainingSession && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm dark:bg-black/75 z-50 flex items-start justify-center overflow-y-auto">
          <div className="w-full max-w-4xl p-4 pt-8 pb-8">
          <TrainingSessionForm
            initialData={selectedTrainingSession}
            isEditing={true}
            onClose={() => setShowEditTrainingModal(false)}
            onSuccess={() => {
              setShowEditTrainingModal(false);
              queryClient.invalidateQueries('training-sessions');
            }}
          />
          </div>
        </div>
      )}
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-600 dark:bg-slate-900 w-full max-w-md"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <TrashIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                Confirmer la suppression
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Êtes-vous sûr de vouloir supprimer {selectedSessions.length} session{selectedSessions.length > 1 ? 's' : ''} ? 
              Cette action est irréversible.
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default SessionsPage;